import { isAuthError, isTransientError } from '@brighte/passwordless-authenticator';
import { datadogRum } from '@datadog/browser-rum';
import { KNOWN_ERROR_MESSAGES as ERROR, KnownErrors, getErrorMessage } from '@utils';
import { ErrorResponse } from '../../graphql';

type ErrorResult = { message: string; code?: number; isRetryable: boolean };

const errorsMap = {
  [KnownErrors.NotFound]: ERROR.INVALID_QUOTE,
  [KnownErrors.ValidationError]: ERROR.VALIDATION_ERROR,
  [KnownErrors.QuoteExpired]: ERROR.QUOTE_EXPIRED,
};

export function getErrorDetails(error: ErrorResponse): ErrorResult {
  datadogRum.addAction('Error Fallback', {
    name: 'Error Fallback',
    values: {
      error,
    },
  });

  if (isAuthError(error)) {
    return { code: 401, message: 'UNAUTHENTICATED', isRetryable: false };
  }

  // A retry has already been attempted (see the mutation's own `retry` option) by the time the
  // customer sees this screen, so `isRetryable` here just decides whether to offer a manual
  // "Try again" instead of relying on the mutation quietly succeeding on its own.
  const isRetryable = isTransientError(error);
  const graphqlError = error?.response?.errors?.[0];

  if (!graphqlError) {
    // No GraphQL errors array at all — typically a raw HTTP failure from the gateway/LB (e.g. a
    // 502) rather than a GraphQL-layer error, so the status lives directly on the response.
    return { code: error?.response?.status, message: ERROR.DEFAULT, isRetryable };
  }

  const errorMessage = getErrorMessage(graphqlError);
  const errorDetails = graphqlError?.extensions;

  //Known errors
  if (errorsMap[errorMessage]) {
    return {
      message: errorsMap[errorMessage],
      isRetryable,
    };
  }

  //Http errors
  if (errorDetails?.response) {
    const { status, body } = errorDetails.response;
    switch (status) {
      case 401:
        return { code: status, message: 'UNAUTHENTICATED', isRetryable: false };
      case 500:
        return { code: status, message: ERROR.INTERNAL_SERVER_ERROR, isRetryable };
      case 404:
        return { code: status, message: ERROR.USER_NOT_FOUND, isRetryable };
      default:
        // Prefer a human-readable backend message; never surface the raw error `code` as the user-facing
        // message (it's a technical identifier). Fall back to the generic "something went wrong" copy and
        // keep the status in `code`, which is rendered separately as "Technical error <code>".
        return { code: status, message: body?.message || ERROR.DEFAULT, isRetryable };
    }
  }

  //Auth errors
  if (errorDetails?.code) {
    switch (errorDetails.code) {
      case 'UNAUTHORIZED':
        return {
          code: errorDetails.statusCode,
          message: errorDetails.message?.includes('suspended') ? ERROR.USER_SUSPENDED : ERROR.FORBIDDEN,
          isRetryable: false,
        };
      case 'UNAUTHENTICATED':
        return { code: errorDetails.statusCode, message: 'UNAUTHENTICATED', isRetryable: false };
      case 'FORBIDDEN':
        return { code: 403, message: ERROR.FORBIDDEN, isRetryable: false };
      default:
        return { message: ERROR.DEFAULT, isRetryable };
    }
  }

  return { message: ERROR.DEFAULT, isRetryable };
}
